// 手牌 / 副露 / 花牌的純佈局數學（Phaser-free）
// 從 CHandView 抽出：所有「config + index → {x,y}」的座標推導集中在此，不碰 sprite、不碰 scene。
// 好處：getMeldPosition 的 6 個 anchor 分支可獨立單元測試（見 tests/ui/hand-layout.test.ts）；
// CHandView 回歸「依 props 建立 / 排列 sprite + 動畫」的職責，方位方向常數也由此單一來源提供。
//
// 註：face（TileFace）相關推導留在 CHandView，因 TileFace 由 tile-sprite 提供（會 import Phaser），
// 放進來會破壞本檔的 Phaser-free 特性。

import type { THandViewConfig } from './hand-view';
import { TILE_BODY_HEIGHT, TILE_BODY_WIDTH } from './tile-geometry';

// 摸進的牌「分離」顯示：排在手牌成長端、與其餘手牌之間的額外空隙（px，pre-scale）
const DRAW_SEPARATE_EXTRA_GAP = 28;
const REVEALED_UPRIGHT_TILE_WIDTH = 90; // bottom / top 公開牌的實際寬度
const REVEALED_SIDE_TILE_HEIGHT = 63; // left / right 公開牌主軸方向的實際高度
const TABLED_BOTTOM_TILE_VISIBLE_WIDTH = 61; // tile-front-down 扣掉左右透明邊後的可見牌身寬度

/** 自摸預覽即時調校值；只套用在推倒後的暗牌與獨立胡張。 */
export interface ISelfDrawHandTuning {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly tileSpacing: number;
  readonly tileScale: number;
}

/** CHandView 的座位方位 const（決定排版方向與牌面朝向）。 */
export const HandPosition = {
  Bottom: 'bottom',
  Top: 'top',
  Left: 'left',
  Right: 'right',
} as const;
export type THandPosition = (typeof HandPosition)[keyof typeof HandPosition];

// 使用自摸預覽鍵盤調校後確認的正式 UI 數值；未指定欄位沿用原本排版計算值。
const SELF_DRAW_HAND_TUNING_OVERRIDES: Record<THandPosition, Partial<ISelfDrawHandTuning>> = {
  [HandPosition.Bottom]: { offsetX: 0, offsetY: 0, tileSpacing: 59 }, // 本家
  [HandPosition.Right]: {
    offsetX: -9,
    offsetY: -19,
    tileSpacing: 31.4,
    tileScale: 0.79,
  }, // 下家
  [HandPosition.Top]: { offsetX: 2, offsetY: 11 }, // 對家
  [HandPosition.Left]: {
    offsetX: 10,
    offsetY: 6,
    tileSpacing: 31.4,
    tileScale: 0.8,
  }, // 上家
};

/**
 * 花牌生長方向 const：第一張花固定在「玩家視角左」，後續往「玩家視角右」延伸；
 * 值描述的是該視角右投射到的「螢幕方向」（四家各不同，見 SEAT_LAYOUT 視角↔螢幕對照）。
 */
export const FlowerGrowDirection = {
  Right: 'right', // 視角右 = 螢幕右（自家 bottom）
  Left: 'left', // 視角右 = 螢幕左（對家 top）
  Up: 'up', // 視角右 = 螢幕上（右家 right）
  Down: 'down', // 視角右 = 螢幕下（左家 left）
} as const;
export type TFlowerGrowDirection = (typeof FlowerGrowDirection)[keyof typeof FlowerGrowDirection];

/** 副露排列軸向 const（覆寫 concealed 軸向）。 */
export const MeldDirection = {
  Horizontal: 'horizontal',
  Vertical: 'vertical',
} as const;
export type TMeldDirection = (typeof MeldDirection)[keyof typeof MeldDirection];

/** 槓第 4 張往哪個方向疊出「凸」字 const。 */
export const MeldKongStackDirection = {
  Up: 'up',
  Down: 'down',
  Left: 'left',
  Right: 'right',
} as const;
export type TMeldKongStackDirection =
  (typeof MeldKongStackDirection)[keyof typeof MeldKongStackDirection];

/** 取得實際使用的 meld 排列方向（沒設定就跟著 position）。 */
export const meldDirectionOf = (config: THandViewConfig): TMeldDirection =>
  config.meldDirection ??
  (config.position === HandPosition.Bottom || config.position === HandPosition.Top
    ? MeldDirection.Horizontal
    : MeldDirection.Vertical);

/** 花牌縮放（fallback meldScale → scale）。 */
export const flowerScaleOf = (config: THandViewConfig): number =>
  config.flowerScale ?? config.meldScale ?? config.scale;

/**
 * 胡牌攤牌時，把暗牌佈局換算成公開牌的實際幾何尺寸。
 *
 * 原本各家的 tileGap 是依牌背／自家大牌設定；直接把 face 換成較窄的副露牌，
 * 會讓中心距離不變、牌身卻變窄，因而出現明顯缺口。這裡維持整列原有跨度，
 * 反算公開牌的 scale，讓「相鄰中心距離 = 公開牌主軸尺寸」，牌張恰好貼合。
 */
const handLayoutForAxisSize = (
  config: THandViewConfig,
  revealedAxisSize: number
): THandViewConfig => {
  const isHorizontal =
    config.position === HandPosition.Bottom || config.position === HandPosition.Top;
  const revealedScale = (config.tileGap * config.scale) / revealedAxisSize;

  return {
    ...config,
    tileGap: revealedAxisSize,
    scale: revealedScale,
    ...(isHorizontal
      ? { concealedTileWidth: revealedAxisSize }
      : { concealedTileHeight: revealedAxisSize }),
  };
};

export const revealedHandLayoutOf = (config: THandViewConfig): THandViewConfig =>
  handLayoutForAxisSize(
    config,
    config.position === HandPosition.Bottom || config.position === HandPosition.Top
      ? REVEALED_UPRIGHT_TILE_WIDTH
      : REVEALED_SIDE_TILE_HEIGHT
  );

/**
 * 自摸攤牌：手牌全部推倒、牌面朝上。
 * 自家由大張正立牌切成 tile-front-down，使用 61px 可見牌身寬度計算中心距，
 * 扣除素材左右透明邊後才會真正貼合；其餘三家本來就沿用公開牌尺寸。
 */
export const tabledHandLayoutOf = (
  config: THandViewConfig,
  tuning?: ISelfDrawHandTuning
): THandViewConfig => {
  const base = handLayoutForAxisSize(
    config,
    config.position === HandPosition.Bottom
      ? TABLED_BOTTOM_TILE_VISIBLE_WIDTH
      : config.position === HandPosition.Top
        ? REVEALED_UPRIGHT_TILE_WIDTH
        : REVEALED_SIDE_TILE_HEIGHT
  );
  if (!tuning) return base;

  const tunedScale = tuning.tileScale;
  // 縮放只改變牌本身尺寸，不應連帶推動整列 anchor；先固定原本第一／最後張中心，
  // 再把 X/Y 位移加到中心位置上，讓四家都能用相同的調校語意。
  const horizontalAxisSize = base.concealedTileWidth ?? TILE_BODY_WIDTH;
  const verticalAxisSize = base.concealedTileHeight ?? TILE_BODY_HEIGHT;
  const concealedLeftEdgeX =
    base.concealedLeftEdgeX === undefined
      ? undefined
      : base.concealedLeftEdgeX +
        (horizontalAxisSize * (base.scale - tunedScale)) / 2 +
        tuning.offsetX;
  const concealedRightEdgeX =
    base.concealedRightEdgeX === undefined
      ? undefined
      : base.concealedRightEdgeX +
        (horizontalAxisSize * (tunedScale - base.scale)) / 2 +
        tuning.offsetX;
  const concealedTopEdgeY =
    base.concealedTopEdgeY === undefined
      ? undefined
      : base.concealedTopEdgeY +
        (verticalAxisSize * (base.scale - tunedScale)) / 2 +
        tuning.offsetY;
  const concealedBottomEdgeY =
    base.concealedBottomEdgeY === undefined
      ? undefined
      : base.concealedBottomEdgeY +
        (verticalAxisSize * (tunedScale - base.scale)) / 2 +
        tuning.offsetY;

  return {
    ...base,
    originX: base.originX + tuning.offsetX,
    originY: base.originY + tuning.offsetY,
    tileGap: tuning.tileSpacing / tunedScale,
    scale: tunedScale,
    ...(concealedLeftEdgeX !== undefined ? { concealedLeftEdgeX } : {}),
    ...(concealedRightEdgeX !== undefined ? { concealedRightEdgeX } : {}),
    ...(concealedTopEdgeY !== undefined ? { concealedTopEdgeY } : {}),
    ...(concealedBottomEdgeY !== undefined ? { concealedBottomEdgeY } : {}),
  };
};

/** 取得某家目前正式排版對應的自摸調校初始值。 */
export const selfDrawHandTuningOf = (config: THandViewConfig): ISelfDrawHandTuning => {
  const tabled = tabledHandLayoutOf(config);
  const overrides = SELF_DRAW_HAND_TUNING_OVERRIDES[config.position];
  return {
    offsetX: overrides.offsetX ?? 0,
    offsetY: overrides.offsetY ?? 0,
    tileSpacing: overrides.tileSpacing ?? tabled.tileGap * tabled.scale,
    tileScale: overrides.tileScale ?? tabled.scale,
  };
};

/**
 * 計算第 N 張暗牌的座標。
 *
 * 解析順序（edge-anchor 優先；同軸只能擇一設定）：
 * - 水平（bottom/top）：concealedLeftEdgeX | concealedRightEdgeX | originX (中心 fallback)
 * - 垂直（left/right）： concealedTopEdgeY  | concealedBottomEdgeY | originY (中心 fallback)
 */
export const tilePositionAt = (
  config: THandViewConfig,
  index: number,
  total: number
): { x: number; y: number } => {
  const step = config.tileGap * config.scale;
  if (config.position === HandPosition.Bottom || config.position === HandPosition.Top) {
    // Horizontal left-anchored：leftmost tile (index=0) 的左緣固定
    if (config.concealedLeftEdgeX !== undefined) {
      const tileWidth = (config.concealedTileWidth ?? TILE_BODY_WIDTH) * config.scale;
      const firstCenterX = config.concealedLeftEdgeX + tileWidth / 2;
      return { x: firstCenterX + index * step, y: config.originY };
    }
    // Horizontal right-anchored：rightmost tile (index=total-1) 的右緣固定
    if (config.concealedRightEdgeX !== undefined) {
      const tileWidth = (config.concealedTileWidth ?? TILE_BODY_WIDTH) * config.scale;
      const lastCenterX = config.concealedRightEdgeX - tileWidth / 2;
      return { x: lastCenterX - (total - 1 - index) * step, y: config.originY };
    }
    return { x: config.originX + index * step, y: config.originY };
  }
  // 垂直方向（left/right）
  // Vertical top-anchored：topmost tile (index=0) 的上緣固定
  if (config.concealedTopEdgeY !== undefined) {
    const tileHeight = (config.concealedTileHeight ?? TILE_BODY_HEIGHT) * config.scale;
    const firstCenterY = config.concealedTopEdgeY + tileHeight / 2;
    return { x: config.originX, y: firstCenterY + index * step };
  }
  // Vertical bottom-anchored：bottommost tile (index=total-1) 的下緣固定
  if (config.concealedBottomEdgeY !== undefined) {
    const tileHeight = (config.concealedTileHeight ?? TILE_BODY_HEIGHT) * config.scale;
    const lastCenterY = config.concealedBottomEdgeY - tileHeight / 2;
    return { x: config.originX, y: lastCenterY - (total - 1 - index) * step };
  }
  return { x: config.originX, y: config.originY + index * step };
};

/**
 * 計算第 N 個 meld slot 的座標（解析順序見各 branch 註解）：
 * 1. 水平 right-anchored 2. 水平 left-anchored 3. 垂直 bottom-anchored
 * 4. 垂直 top-anchored 5. start-anchored（origin） 6. fallback：接在 concealed 後。
 */
export const meldPositionAt = (
  config: THandViewConfig,
  meldIndex: number,
  lastMeldCursor: number,
  concealedCount: number
): { x: number; y: number } => {
  const meldGap = config.meldTileGap ?? config.tileGap;
  const meldScale = config.meldScale ?? config.scale;
  const step = meldGap * meldScale;
  const direction = meldDirectionOf(config);

  // 1. Horizontal right-anchored
  if (
    direction === MeldDirection.Horizontal &&
    config.meldRightEdgeX !== undefined &&
    config.meldOriginY !== undefined
  ) {
    const tileWidth = (config.meldTileWidth ?? TILE_BODY_WIDTH) * meldScale;
    const lastCenterX = config.meldRightEdgeX - tileWidth / 2;
    return { x: lastCenterX - (lastMeldCursor - meldIndex) * step, y: config.meldOriginY };
  }

  // 2. Horizontal left-anchored（leftmost tile 的左緣固定在 meldLeftEdgeX）
  if (
    direction === MeldDirection.Horizontal &&
    config.meldLeftEdgeX !== undefined &&
    config.meldOriginY !== undefined
  ) {
    const tileWidth = (config.meldTileWidth ?? TILE_BODY_WIDTH) * meldScale;
    const firstCenterX = config.meldLeftEdgeX + tileWidth / 2;
    return { x: firstCenterX + meldIndex * step, y: config.meldOriginY };
  }

  // 3. Vertical bottom-anchored
  if (
    direction === MeldDirection.Vertical &&
    config.meldBottomEdgeY !== undefined &&
    config.meldOriginX !== undefined
  ) {
    const tileHeight = (config.meldTileHeight ?? TILE_BODY_HEIGHT) * meldScale;
    const lastCenterY = config.meldBottomEdgeY - tileHeight / 2;
    return { x: config.meldOriginX, y: lastCenterY - (lastMeldCursor - meldIndex) * step };
  }

  // 4. Vertical top-anchored（topmost tile 的上緣固定在 meldTopEdgeY）
  if (
    direction === MeldDirection.Vertical &&
    config.meldTopEdgeY !== undefined &&
    config.meldOriginX !== undefined
  ) {
    const tileHeight = (config.meldTileHeight ?? TILE_BODY_HEIGHT) * meldScale;
    const firstCenterY = config.meldTopEdgeY + tileHeight / 2;
    return { x: config.meldOriginX, y: firstCenterY + meldIndex * step };
  }

  // 5. Start-anchored 模式
  if (config.meldOriginX !== undefined && config.meldOriginY !== undefined) {
    if (direction === MeldDirection.Horizontal) {
      return { x: config.meldOriginX + meldIndex * step, y: config.meldOriginY };
    }
    return { x: config.meldOriginX, y: config.meldOriginY + meldIndex * step };
  }

  // 6. Fallback：接在 concealed 後同方向（舊行為）。slotAfter = concealed 張數 + 1 視覺分隔
  const slotAfter = concealedCount + 1 + meldIndex;
  if (direction === MeldDirection.Horizontal) {
    return { x: config.originX + slotAfter * step, y: config.originY };
  }
  return { x: config.originX, y: config.originY + slotAfter * step };
};

/**
 * 槓的第 4 張往對應方向疊出「凸」字（套用在中間那張的座標上）。
 * 偏移量 = 對應軸的「該方向 tile 尺寸 × meldScale」（或 meldKongStackOffset 指定值）。
 */
export const kongStackOffsetFor = (
  config: THandViewConfig,
  middle: { x: number; y: number },
  meldScale: number
): { x: number; y: number } => {
  const direction = meldDirectionOf(config);
  const stack =
    config.meldKongStackDirection ??
    (direction === MeldDirection.Horizontal
      ? MeldKongStackDirection.Up
      : MeldKongStackDirection.Left);
  // explicit 凸字位移優先；否則用「整張牌尺寸 × meldScale」做 flush stack
  const explicit = config.meldKongStackOffset;
  const tileW = explicit ?? (config.meldTileWidth ?? TILE_BODY_WIDTH) * meldScale;
  const tileH = explicit ?? (config.meldTileHeight ?? TILE_BODY_HEIGHT) * meldScale;
  switch (stack) {
    case MeldKongStackDirection.Up:
      return { x: middle.x, y: middle.y - tileH };
    case MeldKongStackDirection.Down:
      return { x: middle.x, y: middle.y + tileH };
    case MeldKongStackDirection.Left:
      return { x: middle.x - tileW, y: middle.y };
    case MeldKongStackDirection.Right:
      return { x: middle.x + tileW, y: middle.y };
  }
};

/**
 * 第 index 張花在花牌區的座標。第一張（index 0）固定在 origin（玩家視角左），
 * 後續沿 flowerGrowDirection（往視角右投射的螢幕方向）逐張遞增。
 */
export const flowerSlotPositionAt = (
  config: THandViewConfig,
  index: number
): { x: number; y: number } => {
  const scale = flowerScaleOf(config);
  const gap = config.flowerTileGap ?? config.meldTileGap ?? config.tileGap;
  const step = gap * scale;
  const dir =
    config.flowerGrowDirection ??
    (meldDirectionOf(config) === MeldDirection.Horizontal
      ? FlowerGrowDirection.Right
      : FlowerGrowDirection.Down);
  const ox = config.flowerOriginX!;
  const oy = config.flowerOriginY!;
  const dx =
    dir === FlowerGrowDirection.Right ? step : dir === FlowerGrowDirection.Left ? -step : 0;
  const dy = dir === FlowerGrowDirection.Down ? step : dir === FlowerGrowDirection.Up ? -step : 0;
  return { x: ox + index * dx, y: oy + index * dy };
};

/**
 * 把摸進牌放在該家面對牌桌視角的右方：
 * 本家＝螢幕右、下家＝螢幕上、對家＝螢幕左、上家＝螢幕下。
 */
const drawnTileOnPlayerRightPositionAt = (
  config: THandViewConfig,
  restCount: number,
  step: number,
  extra: number
): { x: number; y: number } => {
  if (restCount <= 0) return tilePositionAt(config, 0, 1);
  const distance = step + extra;
  switch (config.position) {
    case HandPosition.Bottom: {
      const last = tilePositionAt(config, restCount - 1, restCount);
      return { x: last.x + distance, y: last.y };
    }
    case HandPosition.Right: {
      const first = tilePositionAt(config, 0, restCount);
      return { x: first.x, y: first.y - distance };
    }
    case HandPosition.Top: {
      const first = tilePositionAt(config, 0, restCount);
      return { x: first.x - distance, y: first.y };
    }
    case HandPosition.Left: {
      const last = tilePositionAt(config, restCount - 1, restCount);
      return { x: last.x, y: last.y + distance };
    }
  }
};

/**
 * 摸進的牌「分離」座標：排在該家玩家視角的手牌右方，再加一個額外空隙。
 * 方位一律以該家面對牌桌的視角為準，固定放在手牌右方。
 * @param restCount 其餘暗牌數（不含摸的那張）
 */
export const drawnSeparatedPositionAt = (
  config: THandViewConfig,
  restCount: number
): { x: number; y: number } => {
  const step = config.tileGap * config.scale;
  const extra = DRAW_SEPARATE_EXTRA_GAP * config.scale;
  return drawnTileOnPlayerRightPositionAt(config, restCount, step, extra);
};

/** 自摸推倒後，胡張仍使用推倒前第 17 張摸進牌的分離位置。 */
export const selfDrawnTilePositionAt = (
  config: THandViewConfig,
  restCount: number,
  tuning?: ISelfDrawHandTuning
): { x: number; y: number } => {
  if (!tuning) return drawnSeparatedPositionAt(config, restCount);

  const tunedLayout = tabledHandLayoutOf(config, tuning);
  const originalExtraGap = DRAW_SEPARATE_EXTRA_GAP * config.scale;
  return drawnTileOnPlayerRightPositionAt(
    tunedLayout,
    restCount,
    tuning.tileSpacing,
    originalExtraGap
  );
};
